import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { Select } from 'antd';
import { Check, CornerDownRight, X } from 'lucide-react';
import { useCallback, useEffect } from 'react';
import { resolveFieldDefaults } from '../core/operators';
import type { Filter, FilterConfig, FilterOperator as FilterOperatorType } from '../core/types';
import { FilterOperator } from './FilterOperator';
import { FilterValue } from './FilterValue';
import { getFilterPopupContainer } from './filter-popup-container';

interface FilterBuilderProps<TWhere> {
    filter: Filter;
    config: FilterConfig<TWhere>;
    whereClause?: TWhere;
    onUpdate: (updates: Partial<Filter>) => void;
    onConfirm: () => void;
    onRemove: () => void;
    autoFocus?: boolean;
}

/**
 * FilterBuilder Component
 *
 * An inline row for editing a filter with the pattern:
 * ↳ where │ [Property ▼] │ [Operator ▼] │ [Value Input] │ ✓ │ ×
 *
 * Used when a FilterChip is clicked/expanded for editing.
 */
export function FilterBuilder<TWhere>({
    filter,
    config,
    whereClause,
    onUpdate,
    onConfirm,
    onRemove,
    autoFocus = false,
}: FilterBuilderProps<TWhere>) {
    const fieldConfig = config.filters.find((f) => f.id === filter.filterId);

    // Handle keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Enter' && !event.shiftKey) {
                // Don't close if focus is in a select dropdown
                const activeElement = document.activeElement;
                const isSelectFocused = activeElement?.closest('.ant-select');
                if (!isSelectFocused) {
                    onConfirm();
                }
            } else if (event.key === 'Escape') {
                onConfirm();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onConfirm]);

    // Handle field change (changing which property to filter on)
    const handleFieldChange = useCallback(
        (newFieldId: string) => {
            const newFieldConfig = config.filters.find((f) => f.id === newFieldId);
            if (newFieldConfig) {
                const { operator, value } = resolveFieldDefaults(newFieldConfig, { preferContains: true });
                onUpdate({ filterId: newFieldId, operator: operator as FilterOperatorType, value });
            }
        },
        [config.filters, onUpdate],
    );

    if (!fieldConfig) return null;

    // Get available fields for the property selector
    const availableFields = config.filters
        .filter((f) => !f.pinned) // Exclude pinned fields from property selector
        .map((f) => ({
            value: f.id,
            label: f.label || f.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

    return (
        <div className="flex items-center gap-3 p-1">
            {/* "where" indicator */}
            <div className="flex shrink-0 items-center gap-1.5 text-muted-foreground text-sm">
                <CornerDownRight size={14} className="text-subtle-foreground" />
                <span className="font-medium">where</span>
            </div>

            {/* Property selector */}
            <Select
                value={filter.filterId}
                onChange={handleFieldChange}
                options={availableFields}
                className="min-w-[160px]"
                size="middle"
                showSearch
                optionFilterProp="label"
                popupMatchSelectWidth={false}
                getPopupContainer={getFilterPopupContainer}
            />

            {/* Operator selector */}
            <FilterOperator
                value={filter.operator}
                fieldType={fieldConfig.type || 'text'}
                customOperators={fieldConfig.operators}
                onChange={(operator) => onUpdate({ operator })}
            />

            {/* Value input */}
            <div className="min-w-[140px]">
                <FilterValue
                    value={filter.value}
                    operator={filter.operator}
                    fieldConfig={fieldConfig}
                    whereClause={whereClause}
                    autoFocus={autoFocus}
                    onChange={(value) => onUpdate({ value })}
                />
            </div>

            {/* Action buttons */}
            <div className="flex shrink-0 items-center gap-0.5">
                {/* Confirm button */}
                <Button
                    type="tertiary"
                    size="small"
                    icon={<Check size={14} />}
                    onClick={onConfirm}
                    className="h-7 w-7 p-0"
                    aria-label="Confirm filter"
                />

                {/* Remove button */}
                <Button
                    type="tertiary"
                    danger
                    size="small"
                    icon={<X size={14} />}
                    onClick={onRemove}
                    className="h-7 w-7 p-0"
                    aria-label="Remove filter"
                />
            </div>
        </div>
    );
}
